/**
 * Dropdown Component
 * Componente de dropdown moderno con animaciones
 */

import { useState, useRef, useEffect, ReactNode, createContext, useContext } from "react";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";

// Context
interface DropdownContextType {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  selectedValue?: string;
  setSelectedValue?: (value: string) => void;
}

const DropdownContext = createContext<DropdownContextType | null>(null);

function useDropdown() {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error("Dropdown components must be used within a Dropdown");
  }
  return context;
}

// Main Dropdown
interface DropdownProps {
  children: ReactNode;
  className?: string;
}

export function Dropdown({ children, className }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <DropdownContext.Provider value={{ isOpen, setIsOpen }}>
      <div ref={dropdownRef} className={cn("relative inline-block", className)}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

// Dropdown Trigger
interface DropdownTriggerProps {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}

export function DropdownTrigger({ children, className, asChild }: DropdownTriggerProps) {
  const { isOpen, setIsOpen } = useDropdown();

  const handleClick = () => setIsOpen(!isOpen);

  if (asChild) {
    return (
      <div onClick={handleClick} className={className}>
        {children}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        "inline-flex items-center justify-between gap-2 px-4 py-2",
        "bg-white border border-gray-200 rounded-xl",
        "text-sm font-medium text-gray-700",
        "shadow-sm transition-all duration-200",
        "hover:bg-gray-50 hover:border-gray-300",
        "focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500",
        className
      )}
      aria-expanded={isOpen}
      aria-haspopup="true"
    >
      {children}
      <ChevronDown
        className={cn(
          "w-4 h-4 text-gray-400 transition-transform duration-200",
          isOpen && "rotate-180"
        )}
      />
    </button>
  );
}

// Dropdown Content
interface DropdownContentProps {
  children: ReactNode;
  className?: string;
  align?: "start" | "center" | "end";
  sideOffset?: number;
}

export function DropdownContent({
  children,
  className,
  align = "start",
  sideOffset = 4,
}: DropdownContentProps) {
  const { isOpen } = useDropdown();

  if (!isOpen) return null;

  const alignStyles = {
    start: "left-0",
    center: "left-1/2 -translate-x-1/2",
    end: "right-0",
  };

  return (
    <div
      className={cn(
        "absolute z-50 mt-1 min-w-[180px]",
        "bg-white rounded-xl border border-gray-100",
        "shadow-lg shadow-gray-200/50",
        "py-1",
        "animate-fade-in-scale origin-top",
        alignStyles[align],
        className
      )}
      style={{ marginTop: sideOffset }}
    >
      {children}
    </div>
  );
}

// Dropdown Item
interface DropdownItemProps {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  icon?: ReactNode;
  selected?: boolean;
  className?: string;
}

export function DropdownItem({
  children,
  onClick,
  disabled = false,
  destructive = false,
  icon,
  selected = false,
  className,
}: DropdownItemProps) {
  const { setIsOpen } = useDropdown();

  const handleClick = () => {
    if (!disabled) {
      onClick?.();
      setIsOpen(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        "flex items-center gap-2 w-full px-3 py-2 text-sm text-left",
        "transition-colors duration-150",
        disabled
          ? "text-gray-400 cursor-not-allowed"
          : destructive
          ? "text-red-600 hover:bg-red-50"
          : "text-gray-700 hover:bg-gray-50",
        selected && "bg-primary-50 text-primary-700",
        className
      )}
    >
      {icon && <span className="w-4 h-4 flex-shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
      {selected && <Check className="w-4 h-4 text-primary-600" />}
    </button>
  );
}

// Dropdown Separator
export function DropdownSeparator({ className }: { className?: string }) {
  return <div className={cn("my-1 h-px bg-gray-100", className)} />;
}

// Dropdown Label
export function DropdownLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider", className)}>
      {children}
    </div>
  );
}

export default Dropdown;
